import { store } from './store';
import { updateOrderItemFields } from './slices/orderSlice';
import { updateOpenOrderItemFields } from './slices/productsSlice';

// Edits to saved order items are shown at once and saved in the background, one at a time per item.
// `confirmed` is what the server last accepted, so a refused edit goes back to it, not to a value that was never saved.
const edits = new Map();

const findRow = (id) => store.getState().orderSlice.orderItems.find((item) => item.id === id);

const show = (id, changes) => {
    store.dispatch(updateOrderItemFields({ id, changes }));
    store.dispatch(updateOpenOrderItemFields({ id, changes }));
};

const pick = (row, keys) => Object.fromEntries(keys.map((key) => [key, row?.[key]]));

export const hasPendingEdits = () => edits.size > 0;

// A reload or closed tab would drop edits that are still waiting to be saved, so the browser asks first.
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (event) => {
        if (!hasPendingEdits()) return;
        event.preventDefault();
        event.returnValue = '';
    });
}

// Edits still being saved for items, to put back on top of an order loaded from the server meanwhile.
export const withPendingEdits = (items) =>
    items.map((item) => (edits.has(item.id) ? { ...item, ...edits.get(item.id).changes } : item));

export const waitForItemEdits = (id) => edits.get(id)?.tail ?? Promise.resolve();

/**
 * Shows `changes` on the item now and saves them with `save(row)`, which builds the request from the
 * item as it is when the turn comes, so quick edits are sent as one. Returns the server answer.
 */
export const editOrderItem = ({ id, changes, save, onRefused }) => {
    const row = findRow(id);
    if (!row) return Promise.resolve({ ack: false });

    const entry = edits.get(id) || { confirmed: pick(row, ['quantity', 'uom', 'price_level', 'unit_price']), version: 0, tail: Promise.resolve(), changes: {} };
    edits.set(id, entry);
    entry.version += 1;
    entry.changes = { ...entry.changes, ...changes };
    const version = entry.version;
    show(id, changes);

    const job = entry.tail.then(async () => {
        // A newer edit of this item is queued and will send the whole state, so this one need not.
        if (entry.version !== version) return { ack: true, merged: true };

        const current = findRow(id);
        const res = current ? await save(current) : { ack: false };
        if (res?.ack) {
            entry.confirmed = pick(current, Object.keys(entry.confirmed));
            return res;
        }
        if (entry.version === version) {
            show(id, entry.confirmed);
            entry.changes = {};
        }
        onRefused?.(res);
        return res;
    });

    entry.tail = job.catch(() => {}).then(() => {
        if (entry.version === version) edits.delete(id);
    });

    return job;
};
